import { MarketingPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getReturnsFacets } from "@redotech/redo-merchant-app-common/client/return";
import { ReviewingPartyFilter } from "@redotech/redo-return-core-common/return-search-filters";
import { createContext, memo, ReactNode, useEffect, useState } from "react";

export interface OrdersReviewCount {
  returnsNeedingReview?: number;
  claimsNeedingReview?: number;
  warrantiesNeedingReview?: number;
  moClaimsNeedingReview?: number;
  roClaimsNeedingReview?: number;
}

export const OrdersReviewCountContext = createContext<
  OrdersReviewCount | undefined
>(undefined);

export const OrdersReviewCountService = memo(function OrdersReviewCountService({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const [returnsNeedingReview, setReturnsNeedingReview] = useState<
    number | undefined
  >(undefined);
  const [claimsNeedingReview, setClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  // Merchant owned claims needing review
  const [moClaimsNeedingReview, setMoClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  // Redo owned claims needing review
  const [roClaimsNeedingReview, setRoClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  const [warrantiesNeedingReview, setWarrantiesNeedingReview] = useState<
    number | undefined
  >(undefined);

  const [_, fetchReturnsNeedingReviewCounts] = useTriggerLoad(
    async (signal) => {
      if (!canViewMarketing) {
        return;
      }
      try {
        const returnsFacets = await getReturnsFacets(client, {
          type: "return",
          signal,
        });
        setReturnsNeedingReview(returnsFacets.byReturnFilterFacet.needs_review);

        const claimsFacets = await getReturnsFacets(client, {
          type: "claim",
          signal,
        });
        setClaimsNeedingReview(claimsFacets.byReturnFilterFacet.needs_review);

        const warrantiesFacets = await getReturnsFacets(client, {
          type: "warranty",
          signal,
        });
        setWarrantiesNeedingReview(
          warrantiesFacets.byReturnFilterFacet.needs_review,
        );
        const moClaimsFacets = await getReturnsFacets(client, {
          type: "claim",
          signal,
          reviewingParty: ReviewingPartyFilter.MERCHANT_REVIEW,
        });
        setMoClaimsNeedingReview(
          moClaimsFacets.byReturnFilterFacet.needs_review,
        );

        const roClaimsFacets = await getReturnsFacets(client, {
          type: "claim",
          signal,
          reviewingParty: ReviewingPartyFilter.NOT_MERCHANT_REVIEW,
        });
        setRoClaimsNeedingReview(
          roClaimsFacets.byReturnFilterFacet.needs_review,
        );
      } catch (error) {
        console.error(
          "Error fetching returns/claims/warranties counts:",
          error,
        );
        setReturnsNeedingReview(undefined);
        setClaimsNeedingReview(undefined);
        setWarrantiesNeedingReview(undefined);
        setMoClaimsNeedingReview(undefined);
        setRoClaimsNeedingReview(undefined);
      }
    },
  );

  useEffect(() => {
    let mounted = true;
    const fetchCounts = () => {
      if (!mounted) {
        return;
      }
      try {
        fetchReturnsNeedingReviewCounts();
      } catch (e: any) {
        if (e.name !== "AbortError") {
          console.error("Error fetching counts:", e);
        }
      }
    };
    fetchCounts();
    const intervalId = setInterval(fetchCounts, 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [client, fetchReturnsNeedingReviewCounts]);

  return (
    <OrdersReviewCountContext.Provider
      value={{
        returnsNeedingReview,
        claimsNeedingReview,
        warrantiesNeedingReview,
        moClaimsNeedingReview,
        roClaimsNeedingReview,
      }}
    >
      {children}
    </OrdersReviewCountContext.Provider>
  );
});
